/**
 * Browser-side Ariregister lookup.
 *
 * We call the public autocomplete endpoint directly from the admin's browser.
 * Why not via Cloud Function: Ariregister filters traffic from Google Cloud
 * IP ranges (403). Admin browsers are on residential/office IPs that pass.
 *
 * This is Phase 1. When the RIK XML-service contract is live, move back into
 * a Cloud Function with credentials in Secret Manager. See docs/OPERATIONS.md
 * 'RIK XML service' section for the migration path.
 */

const BASE_URL = 'https://ariregister.rik.ee/est/api/autocomplete';

interface AriregisterRaw {
  company_id: number;
  reg_code: number;
  name: string;
  historical_names: string[];
  status: string;
  legal_address: string;
  zip_code: string;
  legal_form: string;
  url: string;
}

interface AriregisterResponse {
  status: 'OK' | string;
  data: AriregisterRaw[];
}

export interface AriregisterCompany {
  regCode: string;
  name: string;
  historicalNames: string[];
  statusCode: string;
  statusText: string;
  legalAddress: string;
  zipCode: string;
  legalFormCode: string;
  legalFormText: string;
  url: string;
  isActive: boolean;
  isAtRisk: boolean;
  riskReason?: string;
  fetchedAt: string;
}

const STATUS_TEXT: Record<string, { text: string; active: boolean; risk?: string }> = {
  R: { text: 'Registered', active: true },
  N: { text: 'Deleted', active: false, risk: 'Company removed from registry' },
  L: { text: 'Being liquidated', active: false, risk: 'Company in liquidation' },
  K: { text: 'Deleted', active: false, risk: 'Company removed from registry' },
  P: { text: 'Bankruptcy', active: false, risk: 'Bankruptcy proceedings' },
  S: { text: 'Forced liquidation', active: false, risk: 'Forced liquidation' },
};

const LEGAL_FORM_TEXT: Record<string, string> = {
  '1': 'AS (public limited company)',
  '2': 'TÜ (commercial association)',
  '3': 'UÜ (limited partnership)',
  '5': 'OÜ (private limited company)',
  '6': 'FIE (sole proprietor)',
  '8': 'Non-profit association',
  '9': 'Foundation',
};

function normalize(raw: AriregisterRaw): AriregisterCompany {
  const stat = STATUS_TEXT[raw.status] ?? { text: raw.status, active: false };
  return {
    regCode: String(raw.reg_code),
    name: raw.name,
    historicalNames: raw.historical_names ?? [],
    statusCode: raw.status,
    statusText: stat.text,
    legalAddress: raw.legal_address,
    zipCode: raw.zip_code,
    legalFormCode: raw.legal_form,
    legalFormText: LEGAL_FORM_TEXT[raw.legal_form] ?? raw.legal_form,
    url: raw.url,
    isActive: stat.active,
    isAtRisk: Boolean(stat.risk),
    riskReason: stat.risk,
    fetchedAt: new Date().toISOString(),
  };
}

async function fetchRaw(query: string): Promise<AriregisterRaw[]> {
  const url = `${BASE_URL}?q=${encodeURIComponent(query)}`;
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Ariregister HTTP ${res.status}`);
  }
  const body = (await res.json()) as AriregisterResponse;
  if (body.status !== 'OK') {
    throw new Error(`Ariregister status ${body.status}`);
  }
  return body.data ?? [];
}

export async function searchCompaniesDirect(query: string): Promise<AriregisterCompany[]> {
  const raw = await fetchRaw(query);
  return raw.map(normalize);
}

export async function lookupByRegCodeDirect(regCode: string): Promise<AriregisterCompany | null> {
  const raw = await fetchRaw(regCode);
  const match = raw.find((r) => String(r.reg_code) === regCode);
  return match ? normalize(match) : null;
}
